import React from 'react'
import CustomRow from '../../../../reusable-components/CustomRow'
import { Button, Col, Divider, Empty, Row, Space, Table, Tag, Typography, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faPlus,
  faRefresh,
  faUserMd,
  faChartLine,
  faUserSlash,
  faClipboardCheck,
  faHeartbeat,
  faEye,
  faBookMedical,
  faCancel
} from '@fortawesome/free-solid-svg-icons'
import CustomLoadingIcon from '../../../../reusable-components/CustomLoadingIcon'
import PatientOverview from './PatientCarePlanOverview'
import { EnrolledPatientsTemplates } from './utils/templates'
import { handleError } from '../../../../lib/utils'
import dayjs from 'dayjs'

const { Title, Text } = Typography

// Helper function to format interval days into readable frequency
const formatFrequency = (intervalDays) => {
  if (!intervalDays) return 'Once'

  // Convert days to more readable format
  if (intervalDays >= 365) {
    const years = Math.floor(intervalDays / 365)
    return `${years} Year${years > 1 ? 's' : ''}`
  } else if (intervalDays >= 30) {
    const months = Math.floor(intervalDays / 30)
    return `${months} Month${months > 1 ? 's' : ''}`
  } else if (intervalDays >= 7) {
    const weeks = Math.floor(intervalDays / 7)
    return `${weeks} Week${weeks > 1 ? 's' : ''}`
  } else {
    return `${intervalDays} Day${intervalDays > 1 ? 's' : ''}`
  }
}

const EnrolledPatients = ({
  setNewEnrollment,
  enrolledUsers,
  template,
  setCurrentTemplate,
  handleFetchEnrolledUsers,
  loadingPatients,
  currentPatient,
  setCurrentPatient,
  handleCancelEnrollment,
  handleRefresh
}) => {
  const { token } = theme.useToken()

  // Button style for consistency with main screen
  const buttonStyle = {
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  if (currentPatient) {
    return (
      <div>
        <PatientOverview
          setCurrentPatient={setCurrentPatient}
          currentPatient={currentPatient}
          template={template}
          handleRefresh={handleRefresh}
        />
      </div>
    )
  }

  return (
    <div style={{ background: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <Row gutter={[24, 24]} align='middle' style={{ marginBottom: '24px' }}>
        <Col>
          <Button
            style={{
              ...buttonStyle,
              color: token.colorPrimary,
              borderColor: token.colorPrimary
            }}
            type='primary'
            ghost
            onClick={() => setCurrentTemplate(null)}
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
          >
            Back to Plans
          </Button>
        </Col>
        <Col flex='auto'>
          <Title level={3} style={{ margin: 0 }}>
            {template?.name || 'Care Plan Details'}
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              style={{
                ...buttonStyle,
                background: '#e6f7ff',
                borderColor: '#91d5ff',
                color: '#1890ff'
              }}
              type='primary'
              onClick={() => setNewEnrollment(true)}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              Enroll Patient
            </Button>
            <Button
              style={{
                ...buttonStyle,
                background: '#f6ffed',
                borderColor: '#b7eb8f',
                color: '#52c41a'
              }}
              onClick={handleFetchEnrolledUsers}
              icon={<FontAwesomeIcon icon={faRefresh} />}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <div
            style={{
              background: token.colorBgContainer,
              padding: '20px',
              borderRadius: '8px',
              border: `1px solid ${token.colorBorderSecondary}`
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <Title
                level={4}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: token.colorPrimary
                }}
              >
                <FontAwesomeIcon icon={faUserMd} style={{ marginRight: '10px' }} />
                Required Visits
              </Title>
              <Divider style={{ margin: '12px 0' }} />

              {template?.tracking?.filter((i) => i.trackerType === 'visits')?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {template?.tracking
                    ?.filter((i) => i.trackerType === 'visits')
                    ?.map((tracker, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          background: token.colorBgContainerDisabled,
                          borderRadius: '6px'
                        }}
                      >
                        <div>
                          <Text strong style={{ display: 'block' }}>
                            {tracker.dataLabel}
                          </Text>
                          <Text type='secondary'>Frequency: Every {formatFrequency(tracker.intervalDays)}</Text>
                        </div>
                        <Tag color='blue' style={{ marginLeft: 'auto' }}>
                          {tracker.count || 1} visit{tracker.count > 1 || !tracker.count ? 's' : ''}
                        </Tag>
                      </div>
                    ))}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '30px 10px',
                    background: token.colorBgContainerDisabled,
                    borderRadius: '8px'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faClipboardCheck}
                    style={{
                      fontSize: '36px',
                      color: token.colorPrimary,
                      opacity: 0.5,
                      marginBottom: '12px'
                    }}
                  />
                  <Text style={{ color: token.colorTextSecondary }}>No visits required for this plan</Text>
                </div>
              )}
            </div>

            <div>
              <Title
                level={4}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: token.colorPrimary
                }}
              >
                <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '10px' }} />
                Health Metrics
              </Title>
              <Divider style={{ margin: '12px 0' }} />

              {template?.tracking?.filter((i) => i.trackerType === 'data')?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {template?.tracking
                    ?.filter((i) => i.trackerType === 'data')
                    ?.map((tracker, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          background: token.colorBgContainerDisabled,
                          borderRadius: '6px'
                        }}
                      >
                        <div>
                          <Text strong style={{ display: 'block' }}>
                            {tracker.dataLabel}
                          </Text>
                          <Text type='secondary'>Frequency: Every {formatFrequency(tracker.intervalDays)}</Text>
                        </div>
                        <Tag color='green' style={{ marginLeft: 'auto' }}>
                          {tracker.count || 1} reading{tracker.count > 1 || !tracker.count ? 's' : ''}
                        </Tag>
                      </div>
                    ))}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '30px 10px',
                    background: token.colorBgContainerDisabled,
                    borderRadius: '8px'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faHeartbeat}
                    style={{
                      fontSize: '36px',
                      color: token.colorSuccess,
                      opacity: 0.5,
                      marginBottom: '12px'
                    }}
                  />
                  <Text style={{ color: token.colorTextSecondary }}>No health metrics tracked in this plan</Text>
                </div>
              )}
            </div>
          </div>
        </Col>

        <Col xs={24} md={16}>
          <div
            style={{
              height: '100%',
              background: token.colorBgContainer,
              padding: '20px',
              borderRadius: '8px',
              border: `1px solid ${token.colorBorderSecondary}`
            }}
          >
            <Title
              level={4}
              style={{
                display: 'flex',
                alignItems: 'center',
                color: token.colorPrimary,
                marginBottom: '16px'
              }}
            >
              Enrolled Patients
            </Title>

            <CustomLoadingIcon
              loadingText='Loading Patients...'
              loading={loadingPatients}
              content={
                !loadingPatients && enrolledUsers && enrolledUsers.length > 0 ? (
                  <Table
                    size='middle'
                    bordered
                    dataSource={enrolledUsers}
                    columns={[
                      {
                        title: 'Patient',
                        key: 'patient',
                        render: (record) => `${record.patientProfile.firstName} ${record.patientProfile.lastName}`
                      },
                      {
                        title: 'ID Number',
                        key: 'idNumber',
                        render: (record) => `${record.patientProfile.idNo}`
                      },
                      {
                        title: 'Enrolled',
                        key: 'enrolled',
                        render: (record) => dayjs(record.enrolledOn).format('DD MMM YYYY')
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (record) => (
                          <Space>
                            <Button
                              type='text'
                              title='View patient details'
                              onClick={() => {
                                setCurrentPatient(record)
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                style={{
                                  fontSize: '18px',
                                  color: token.colorPrimary
                                }}
                              />
                            </Button>
                            <Button type='text' title='View medical records'>
                              <FontAwesomeIcon
                                icon={faBookMedical}
                                style={{
                                  fontSize: '18px',
                                  color: token.colorInfo
                                }}
                              />
                            </Button>
                            <Button
                              type='text'
                              title='Cancel enrollment'
                              onClick={() => {
                                handleCancelEnrollment(record)
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faCancel}
                                style={{
                                  fontSize: '18px',
                                  color: token.colorError
                                }}
                              />
                            </Button>
                          </Space>
                        )
                      }
                    ]}
                    pagination={{ pageSize: 10 }}
                    scroll={{ y: 400 }}
                  />
                ) : !loadingPatients ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 20px',
                      background: token.colorBgContainerDisabled,
                      borderRadius: '8px'
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faUserSlash}
                      style={{
                        fontSize: '64px',
                        color: token.colorWarning,
                        opacity: 0.5,
                        marginBottom: '24px'
                      }}
                    />
                    <Title level={5} style={{ margin: 0, marginBottom: '8px', color: token.colorText }}>
                      No Enrolled Patients
                    </Title>
                    <Text style={{ color: token.colorTextSecondary, textAlign: 'center' }}>
                      There are currently no patients enrolled in this care plan.
                      <br />
                      Use the "Enroll Patient" button above to add patients.
                    </Text>
                  </div>
                ) : null
              }
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default EnrolledPatients
